/**
 * Splits a contact number into phone number and extension parts.
 *
 * @param {string} val - The contact number to split. It may include an extension separated by ' ext. '.
 * @returns {Object} An object containing the phone number and extension parts.
 * - phone_number {string}: The extracted phone number. If no phone number is found, an empty string is returned.
 * - ext {string}: The extracted extension. If no extension is found, an empty string is returned.
 */
export const splitContactNumber = (val) => {
  if (typeof val !== "string") return { phone_number: "", ext: "" };
  const parts = val.split(" ext. ");
  return {
    phone_number: parts[0] ?? "",
    ext: parts[1] ?? "",
  };
};

export const splitHomeContactNumber = (val) => {
  if (typeof val !== "string") return { home_phone_number: "", ext: "" };
  const parts = val.split(" ext. ");
  return {
    home_phone_number: parts[0] ?? "",
    ext: parts[1] ?? "",
  };
};

/**
 * Creates a formatted contact number from the given values.
 *
 * @param {Object} values - The values used to create the contact number.
 * - values.phone_number - The phone number to include in the contact number. If not provided, an empty string is assumed.
 * - values.ext - The extension to include in the contact number. If not provided, it will be omitted from the contact number.
 * @returns {string} The formatted contact number combining the phone number and extension (if available) otherwise empty string.
 */
export const createContactNumber = (values = {}) => {
  const phone = typeof values.phone_number === "string" ? values.phone_number : "";
  const ext = typeof values.ext === "string" ? values.ext : "";
  return ext ? `${phone} ext. ${ext}` : phone;
};

/**
 * Calculates the length of a formatted phone number by removing dashes and underscores.
 *
 * @param {string} val - The formatted phone number to measure. Dashes (-) and underscores (_) will be removed before calculating the length.
 * @returns {number} The length of the formatted phone number after removing dashes and underscores.
 */
export function formatStoredPhoneForDisplay(value) {
  const raw = typeof value === "string" ? value.trim() : "";
  if (/^\d{10}$/.test(raw)) return `(${raw.slice(0, 3)}) ${raw.slice(3, 6)}-${raw.slice(6)}`;
  return raw;
}

export const formattedPhoneNumberLength = (val) => {
  if (typeof val !== "string") return 0;
  return val.replace(/-|_/g, "").length;
};

/** Strip +1 / country code for display; show 10-digit NANP local number only. */
export const formatEscalationNumberForDisplay = (val) => {
  if (!val || typeof val !== "string") return "";
  const digits = val.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    return digits.slice(1);
  }
  if (digits.length === 10) {
    return digits;
  }
  return digits.slice(-10);
};

/** Prefix +1 for E.164 storage when user enters a 10-digit NANP number. */
export const formatEscalationNumberForSubmit = (val) => {
  const trimmed = (val || "").trim();
  if (!trimmed) return "";
  const digits = trimmed.replace(/\D/g, "");
  if (digits.length === 10) {
    return `+1${digits}`;
  }
  if (digits.length === 11 && digits.startsWith("1")) {
    return `+${digits}`;
  }
  if (trimmed.startsWith("+")) {
    return trimmed;
  }
  return "";
};